/**
 * @file useTelemetry.js
 * @description Hook to retrieve a specific device's real-time telemetry from
 *              the global DeviceStore context. Computes device status as
 *              'active' (< 5 min), 'stale' (> 5 min), or 'unknown'.
 */
import { useMemo } from 'react';
import { useDeviceStore } from '../store/DeviceStore';

const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 Minutes

const useTelemetry = (deviceId) => {
    const { state } = useDeviceStore();

    const device = useMemo(() => {
        return state.devices.find(d => d.device_id === deviceId || d.equipment_id === deviceId);
    }, [state.devices, deviceId]);

    const status = useMemo(() => {
        if (!device) return 'unknown';

        const lastUpdate = device.timestamp || device.last_update || device.created_at;
        if (!lastUpdate) return 'unknown';

        const diff = Date.now() - new Date(lastUpdate).getTime();

        if (diff > STALE_THRESHOLD_MS) return 'stale'; // > 5 mins
        return 'active'; // < 5 mins
    }, [device]);

    return {
        device,
        status, // 'active' | 'stale' | 'unknown'
        loading: state.loading && !device
    };
};

export default useTelemetry;
